// src/utils/chunk-reload.ts
// Recovery for stale lazy-chunk loads after a frontend deploy.
//
// Routes are code-split via React.lazy(() => import(...)); each chunk ships under a
// content-hashed filename. A new deploy replaces those hashes, so a browser tab still
// running the previous build asks the CDN for a chunk that no longer exists. The
// dynamic import() then rejects — "Failed to fetch dynamically imported module"
// (Chromium) or "Importing a module script failed." (Safari) — and on an SPA host that
// rewrites a missing /assets/* to index.html, the browser refuses to run HTML as a
// module. This is a deploy-timing artifact, not a code bug; the fix is to reload once
// to the current build (fresh index.html + current chunk hashes).

const RELOAD_GUARD_KEY = 'app:chunk-reload-ts';

// A repeat failure within this window means the reload did not help (a genuinely broken
// build, not just a stale tab) — stop reloading and let the error surface instead of looping.
const RELOAD_GUARD_WINDOW_MS = 15_000;

/** Message signatures of a failed dynamic import / preload across browsers. */
const CHUNK_ERROR_PATTERN =
    /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|unable to preload css|dynamically imported module/i;

/** True when the error is a stale/failed chunk load rather than an application bug. */
export function isChunkLoadError(error: unknown): boolean {
    const message =
        error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return CHUNK_ERROR_PATTERN.test(message);
}

/**
 * Reload once to recover a stale chunk. Guarded by a sessionStorage timestamp so a
 * deploy that is actually broken (the chunk 404s even on the fresh build) reloads at
 * most once per {@link RELOAD_GUARD_WINDOW_MS} rather than looping.
 */
export function reloadForStaleChunk(): void {
    if (typeof window === 'undefined') {
        return;
    }
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
    if (Number.isFinite(last) && Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
        return;
    }
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    window.location.reload();
}
